// Recording the learner. The recording stays in memory as a blob URL, is never uploaded,
// and is released when the next one replaces it or the card is left.

export const MAX_RECORDING_MS = 15_000;
const PEAKS = 48;

/** 'insecure': the page isn't on https, so browsers switch the microphone off. */
export type MicAccess = 'insecure' | 'unsupported' | 'prompt' | 'granted' | 'denied';

export interface RecordingResult {
  url: string;
  /** The recording itself, for analysis on the device (fetching a blob: URL is blocked by the site's security policy). */
  blob: Blob;
  duration: number;
  peaks: number[];
  /** Where the speech starts and ends, in seconds, so playback can skip the silence around it. */
  speech: { from: number; to: number };
}

export interface RecordingHandle {
  stop(): void;
  cancel(): void;
  result: Promise<RecordingResult | null>;
}

interface StartOptions {
  autoStop: boolean;
  onLevel?: (level: number) => void;
  /** The recording ended by itself (silence, time limit) or was interrupted. */
  onEnd?: (reason: 'silence' | 'limit' | 'interrupted' | 'stopped') => void;
}

type AudioSessionNavigator = Navigator & { audioSession?: { type: string } };

function setAudioSession(type: string) {
  try {
    const session = (navigator as AudioSessionNavigator).audioSession;
    if (session) session.type = type;
  } catch {
    // Not supported.
  }
}

export function micSupported(): boolean {
  return typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia && typeof MediaRecorder !== 'undefined';
}

export async function micAccess(): Promise<MicAccess> {
  if (typeof window !== 'undefined' && window.isSecureContext === false) return 'insecure';
  if (!micSupported()) return 'unsupported';
  try {
    const status = await navigator.permissions.query({ name: 'microphone' as PermissionName });
    return status.state as MicAccess;
  } catch {
    return 'prompt';
  }
}

function pickMimeType(): string | undefined {
  // Safari records MP4/AAC natively (and plays it everywhere); Chrome and Firefox do best with
  // Opus in WebM.
  const apple = typeof navigator !== 'undefined' && /Apple/.test(navigator.vendor);
  const types = apple
    ? ['audio/mp4', 'audio/webm;codecs=opus', 'audio/webm']
    : ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'];
  return types.find((t) => MediaRecorder.isTypeSupported?.(t));
}

/** Bars of loudness plus where the speech sits, from a decoded recording. */
export function analyse(samples: Float32Array, sampleRate: number): Pick<RecordingResult, 'peaks' | 'speech' | 'duration'> {
  const duration = samples.length / sampleRate;
  const size = Math.max(1, Math.floor(samples.length / PEAKS));
  const rms: number[] = [];
  for (let i = 0; i < PEAKS; i++) {
    let sum = 0;
    const end = Math.min(samples.length, (i + 1) * size);
    for (let j = i * size; j < end; j++) sum += samples[j] * samples[j];
    rms.push(Math.sqrt(sum / Math.max(1, end - i * size)));
  }
  const max = Math.max(...rms, 1e-6);
  const peaks = rms.map((v) => Math.round((v / max) * 100));
  const threshold = 12;
  const first = peaks.findIndex((p) => p >= threshold);
  const last = peaks.length - 1 - [...peaks].reverse().findIndex((p) => p >= threshold);
  const bar = duration / PEAKS;
  const speech =
    first < 0 ? { from: 0, to: duration } : { from: Math.max(0, (first - 1) * bar), to: Math.min(duration, (last + 2) * bar) };
  return { peaks, speech, duration };
}

/**
 * Asks for the microphone (the browser prompts the first time) and starts recording.
 * Throws a DOMException named NotAllowedError when access is refused.
 */
export async function startRecording(options: StartOptions): Promise<RecordingHandle> {
  // Created before any await so it counts as part of the tap on iOS.
  const AudioCtx = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
  const ctx = new AudioCtx();
  setAudioSession('play-and-record');
  let stream: MediaStream;
  try {
    // Browsers turn on echo cancellation, noise suppression and gain control by default.
    stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  } catch (err) {
    ctx.close().catch(() => undefined);
    setAudioSession('auto');
    throw err;
  }
  if (ctx.state === 'suspended') await ctx.resume().catch(() => undefined);

  const mimeType = pickMimeType();
  let recorder: MediaRecorder;
  try {
    recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
  } catch (err) {
    for (const track of stream.getTracks()) track.stop();
    ctx.close().catch(() => undefined);
    setAudioSession('auto');
    throw err;
  }
  const chunks: Blob[] = [];
  recorder.ondataavailable = (e) => {
    if (e.data.size > 0) chunks.push(e.data);
  };

  const source = ctx.createMediaStreamSource(stream);
  const analyser = ctx.createAnalyser();
  analyser.fftSize = 1024;
  source.connect(analyser);
  const buffer = new Float32Array(analyser.fftSize);

  let cancelled = false;
  let ended = false;
  let endReason: 'silence' | 'limit' | 'interrupted' | 'stopped' = 'stopped';
  let raf = 0;
  const startedAt = performance.now();
  let floor = Infinity;
  let speechMs = 0;
  let silenceMs = 0;
  let last = startedAt;

  const cleanUp = () => {
    cancelAnimationFrame(raf);
    clearTimeout(limit);
    document.removeEventListener('visibilitychange', onHidden);
    for (const track of stream.getTracks()) track.stop();
    source.disconnect();
    ctx.close().catch(() => undefined);
    setAudioSession('auto');
  };

  const MIN_MS = 600;
  const finish = (reason: typeof endReason) => {
    if (ended) return;
    // A tap on stop straight after start would give an empty recording: keep going a moment.
    const elapsed = performance.now() - startedAt;
    if (reason === 'stopped' && !cancelled && elapsed < MIN_MS) {
      setTimeout(() => finish(reason), MIN_MS - elapsed);
      return;
    }
    ended = true;
    endReason = reason;
    if (recorder.state !== 'inactive') recorder.stop();
    else cleanUp();
  };

  const loop = () => {
    const now = performance.now();
    const dt = now - last;
    last = now;
    analyser.getFloatTimeDomainData(buffer);
    let sum = 0;
    for (let i = 0; i < buffer.length; i++) sum += buffer[i] * buffer[i];
    const rms = Math.sqrt(sum / buffer.length);
    const elapsed = now - startedAt;
    if (elapsed < 300) floor = Math.min(floor, rms || floor);
    const threshold = Math.max(0.015, (Number.isFinite(floor) ? floor : 0.005) * 3);
    if (rms > threshold) {
      speechMs += dt;
      silenceMs = 0;
    } else if (speechMs > 250) {
      silenceMs += dt;
    }
    options.onLevel?.(Math.min(1, rms / 0.25));
    if (options.autoStop && speechMs > 250 && silenceMs > 1300 && elapsed > 900) {
      finish('silence');
      return;
    }
    raf = requestAnimationFrame(loop);
  };

  const onHidden = () => {
    if (document.visibilityState === 'hidden') {
      cancelled = true;
      finish('interrupted');
    }
  };
  document.addEventListener('visibilitychange', onHidden);
  for (const track of stream.getTracks()) {
    track.addEventListener('ended', () => {
      cancelled = true;
      finish('interrupted');
    });
  }
  const limit = setTimeout(() => finish('limit'), MAX_RECORDING_MS);

  const result = new Promise<RecordingResult | null>((resolve) => {
    recorder.onstop = async () => {
      cleanUp();
      options.onEnd?.(endReason);
      if (cancelled || chunks.length === 0) return resolve(null);
      const blob = new Blob(chunks, { type: recorder.mimeType || mimeType || 'audio/webm' });
      const url = URL.createObjectURL(blob);
      const fallback = (performance.now() - startedAt) / 1000;
      const decoder = new AudioCtx();
      try {
        const audio = await decoder.decodeAudioData(await blob.arrayBuffer());
        resolve({ url, blob, ...analyse(audio.getChannelData(0), audio.sampleRate) });
      } catch {
        resolve({ url, blob, duration: fallback, peaks: [], speech: { from: 0, to: fallback } });
      } finally {
        decoder.close().catch(() => undefined);
      }
    };
  });

  recorder.start();
  raf = requestAnimationFrame(loop);

  return {
    stop: () => finish('stopped'),
    cancel: () => {
      cancelled = true;
      finish('stopped');
    },
    result,
  };
}
